import React, { useEffect, useState } from "react";
import { Button, Col, Modal, Row, Table } from "react-bootstrap";
import detailsgql from "gql/dashboard/sales/details.gql";
import { useQuery } from "@apollo/client";
import DateFormat from "components/DateFormat";
import { useAppContext } from "contexts/AppContext";
import DefaultCurrency from "components/DefaultCurrency";
import PercentFormat from "components/PercentFormat";
import StatusFormat from "components/StatusFormat";
import Loading from "components/Loading";
import { PencilFill, Trash } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import useAxios from "hooks/useAxios";
import { toast } from "react-toastify";
import useAlert from "hooks/useAlert";

const Details = ({ order, setOrder, edit, remove, draft = false }) => {
  // contexts
  const { appState } = useAppContext();

  // states
  const [show, setShow] = useState(true);
  const [completeOrder, setCompleteOrder] = useState({});

  // hooks
  const { loading: l, data: d, error: e, post, put } = useAxios();
  const navigate = useNavigate();
  const { loading, data, error } = useQuery(detailsgql, {
    variables: { id: order.id },
    skip: draft,
  });
  const alert = useAlert();

  // functions
  const continueSubmit = (email = false) => {
    const data = {
      id: completeOrder.id,
      sales_man: completeOrder.salesMan.id,
      doc_date: data.id ? completeOrder.docDate : new Date().toISOString().split("T")[0],
      deliveryDate: completeOrder.deliveryDate,
      customer: completeOrder.customer.id,
      customer_branch: completeOrder.customerBranch?.id,
      customer_email: (email && completeOrder.customerEmail) || null,
      customerOrderNumber: completeOrder.customerPONumber,
      paymentTerms: completeOrder.paymentTerms.id,
      deliveryPoint: completeOrder.customerBranch?.address,
      terms: completeOrder.terms,
      labeling: completeOrder.labeling,
      remarks: completeOrder.remarks,
      draft: completeOrder.draft,
      customer_signature: completeOrder.customerSignature,
      sales_man_signature: completeOrder.salesmanSignature,
      details: completeOrder.details
        .filter((x) => x.item)
        .map((cartItem) => ({
          id: cartItem.id,
          deliveryDate: completeOrder.deliveryDate,
          item: cartItem.item.id,
          quantity: cartItem.quantity,
          deliveryPoint: completeOrder.customerBranch?.address,
          sales_price: cartItem.salesPrice,
          extra: !!cartItem.extra,
        })),
    };
    if (data.id) put(`/sales/salesorders/${data.id}/`, data);
    else post("/sales/salesorders/", data);
  };
  const submit = () => {
    if (!completeOrder.id && completeOrder.customerEmail) {
      alert
        .fire({
          title: `Email The Document`,
          html: `Do you want to email this sales order to <b>${completeOrder.customer.name}</b>?<br /><br /><span class="text-muted">Email: ${completeOrder.customerEmail}</span>`,
          icon: "question",
          showDenyButton: true,
          confirmButtonText: "Yes",
          denyButtonText: "No",
        })
        .then((confirmed) => {
          continueSubmit(confirmed.isConfirmed);
        });
    } else {
      continueSubmit();
    }
  };

  // effects
  useEffect(() => {
    if (!loading && data) {
      const so = data.salesOrders[0];
      so.paymentTerms = appState.resources.paymentTerms.find(
        (x) => x.id == so.paymentTerms.id
      );
      so.customer = appState.resources.companies.find(
        (x) => x.id == so.customer.id
      );
      so.salesOrderDetails.forEach((detail) => {
        detail.item =
          appState.resources.items.find((x) => x.id === detail.item?.id) ||
          detail.item;
      });
      so.details = so.salesOrderDetails;
      setCompleteOrder(so);
    }
  }, [loading]);

  useEffect(() => {
    if (order) {
      setShow(true);
      if (draft) {
        const o = {
          ...order,
          status: "Outbox",
          progress: 0,
          details: order.details
            .filter((x) => x.item)
            .map((detail) => ({
              ...detail,
              item: appState.resources.items.find((x) => x.id === detail.item),
              uom: appState.resources.items.find((x) => x.id === detail.item)
                ?.uom,
            })),
        };
        setCompleteOrder(o);
      }
    } else {
      setShow(false);
    }
  }, [order]);

  useEffect(() => {
    if (!l && d) {
      toast.success(`Document was created successfully.`);
      const sales = JSON.parse(localStorage.getItem("sales") || "[]");
      localStorage.setItem(
        "sales",
        JSON.stringify(sales.filter((x) => x.dId != order.dId))
      );

      navigate(`/dashboard/sales/orders?open=${d.data.data.id}`);
    }
  }, [l]);

  return (
    <React.Fragment>
      <Modal
        size="lg"
        show={show}
        onHide={() => {
          setCompleteOrder({});
          setShow(false);
          setOrder(null);
        }}
      >
        <Modal.Header closeButton>
          {order.docNumber || completeOrder.docNumber}
        </Modal.Header>
        <Modal.Body className="h-80vh overflow-auto">
          {loading && <Loading />}
          {!loading && (
            <React.Fragment>
              <Row className="justify-content-between">
                <span className="w-auto">
                  {draft && (
                    <Button size="sm" onClick={submit} disabled={l}>
                      {!l && "Re-submit"}
                      {l && <Loading />}
                    </Button>
                  )}
                </span>
                <span className="w-auto">
                  <Button className="mx-1" variant="warning" size="sm">
                    <PencilFill
                      color="white"
                      onClick={() => edit(completeOrder)}
                      size={16}
                    />
                  </Button>
                  <Button className="mx-1" variant="danger" size="sm">
                    <Trash onClick={() => remove(completeOrder)} size={16} />
                  </Button>
                </span>
              </Row>
              <Row>
                <Col xs={6} sm={3}>
                  Status:
                </Col>
                <Col xs={6} sm={3}>
                  <StatusFormat value={completeOrder.status} />
                </Col>
                <Col xs={6} sm={3}>
                  Progress:
                </Col>
                <Col xs={6} sm={3}>
                  <PercentFormat value={completeOrder.progress} />
                </Col>
              </Row>
              <Row>
                <Col xs={6} sm={3}>
                  Doc Number:
                </Col>
                <Col xs={6} sm={3}>
                  {completeOrder.docNumber}
                </Col>
                <Col xs={6} sm={3}>
                  Doc Date:
                </Col>
                <Col xs={6} sm={3}>
                  <DateFormat value={completeOrder.docDate} />
                </Col>
              </Row>
              <Row>
                <Col xs={6} sm={3}>
                  Customer:
                </Col>
                <Col xs={6} sm={3}>
                  {completeOrder.customer?.name}
                </Col>
                <Col xs={6} sm={3}>
                  Delivery Address:
                </Col>
                <Col xs={6} sm={3}>
                  {completeOrder.customerBranch?.address}
                </Col>
              </Row>
              <Row>
                <Col xs={6} sm={3}>
                  Payment Term:
                </Col>
                <Col xs={6} sm={3}>
                  {completeOrder.paymentTerms?.name}
                </Col>
                <Col xs={6} sm={3}>
                  Remarks:
                </Col>
                <Col xs={6} sm={3}>
                  {completeOrder.remarks}
                </Col>
              </Row>
              <Row className="mt-5 overflow-auto h-50vhoverflow-auto">
                <Col xs={12} className="mb-3">
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Qty</th>
                        <th>UoM</th>
                        <th>Price</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completeOrder.details?.map((detail, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            {detail.item?.displayName}
                            <p className="text-muted m-0">
                              {detail.item?.secondaryDisplayName}
                            </p>
                          </td>
                          <td>{detail.quantity}</td>
                          <td>{detail.uom?.name}</td>
                          <td>
                            <DefaultCurrency value={detail.salesPrice} />
                          </td>
                          <td>
                            <DefaultCurrency
                              value={detail.quantity * detail.salesPrice}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
                <Col xs={4} md={6} lg={8}></Col>
                <Col xs={8} md={6} lg={4}>
                  <Row>
                    <Col className="text-start">Total Qty:</Col>
                    <Col className="text-end">
                      {completeOrder?.details?.reduce(
                        (pre, cur) => pre + cur.quantity,
                        0
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-start">Total:</Col>
                    <Col className="text-end">
                      <DefaultCurrency
                        value={completeOrder?.details?.reduce(
                          (pre, cur) => pre + cur.quantity * cur.salesPrice,
                          0
                        )}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </React.Fragment>
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default Details;
